// <Callout> — inline help / tonal hint card.
//
// Per GOOGLE_DESIGN_PATTERN_ANALYSIS.md §"Inline help" + DESIGN_INVENTORY.md
// "Callout patterns": tonal container, role icon, persistent by default,
// optional one primary CTA.

import type { ReactNode } from 'react';
import { tokens } from '../tokens';
import { Icon, type IconName } from './Icon';

export interface CalloutProps {
  severity: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children: ReactNode;
  icon?: IconName;
  action?: { label: string; onClick(): void };
  className?: string;
  'data-testid'?: string;
}

const SEVERITY_BG: Record<CalloutProps['severity'], string> = {
  info: tokens.color.surface[2],
  success: tokens.color.surface[2],
  warning: tokens.color.surface[2],
  error: tokens.color.surface[2],
};

const SEVERITY_ACCENT: Record<CalloutProps['severity'], string> = {
  info: tokens.color.semantic.info,
  success: tokens.color.semantic.success,
  warning: tokens.color.semantic.warning,
  error: tokens.color.semantic.danger,
};

const SEVERITY_ICON: Record<CalloutProps['severity'], IconName> = {
  info: 'info',
  success: 'check_circle',
  warning: 'warning',
  error: 'error',
};

export function Callout({
  severity,
  title,
  children,
  icon,
  action,
  className,
  'data-testid': testId,
}: CalloutProps) {
  return (
    <div
      role="note"
      data-testid={testId}
      data-severity={severity}
      className={className}
      style={{
        display: 'flex',
        gap: tokens.space[2],
        padding: tokens.space[3],
        background: SEVERITY_BG[severity],
        color: tokens.color.ink.primary,
        borderLeft: `4px solid ${SEVERITY_ACCENT[severity]}`,
        borderRadius: tokens.radius.md,
        fontFamily: tokens.font.family.ui,
        fontSize: tokens.font.role.body.size,
        lineHeight: tokens.font.role.body.lineHeight,
      }}
    >
      <Icon name={icon ?? SEVERITY_ICON[severity]} size={20} decorative />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        {title ? (
          <div style={{ fontWeight: tokens.font.weight.semibold, marginBottom: tokens.space[1] }}>
            {title}
          </div>
        ) : null}
        <div style={{ color: tokens.color.ink.secondary }}>{children}</div>
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            style={{
              marginTop: tokens.space[2],
              appearance: 'none',
              background: 'transparent',
              color: SEVERITY_ACCENT[severity],
              border: 0,
              padding: 0,
              cursor: 'pointer',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              fontWeight: tokens.font.weight.medium,
              textDecoration: 'underline',
            }}
          >
            {action.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}
